<template>
  <div class="px-8 py-6">
    <div class="d-flex align-center justify-space-between flex-wrap ga-3 mb-5">
      <div>
        <h1 class="page-title mb-1">{{ isArchived ? 'Users Up for Deletion' : 'Users' }}</h1>
        <p class="body-text text-medium-emphasis mt-2 mb-0">
          {{ isArchived ? 'Manage user accounts scheduled for deletion.' :
            'View and manage all user accounts in your system.'
          }}
        </p>
      </div>
      <page-breadcrumbs v-if="isArchived" :items="breadcrumbItems" />
    </div>

    <v-row class="mb-2 align-center">
      <v-col cols="12" sm="6" md="5">
        <v-text-field v-model="pagination.search" density="compact" label="Search by name or email" clearable
          prepend-inner-icon="mdi-magnify" variant="outlined" hide-details>
          <template #append-inner>
            <UserFilterMenu v-model="filters" :role-options="roleFilterOptions" :status-options="statusFilterOptions"
              :payment-options="paymentFilterOptions" :plan-options="planFilterOptions" />
          </template>
        </v-text-field>
      </v-col>
      <v-spacer />
      <v-col v-if="!isArchived" cols="auto">
        <v-btn prepend-icon="mdi-delete-clock" color="error" variant="outlined"
          @click="$router.push({ name: 'users.archive.list' })">
          View Deletion Queue
        </v-btn>
      </v-col>
    </v-row>

    <UserFilterChips v-model="filters" :role-options="roleFilterOptions" :status-options="statusFilterOptions"
      :payment-options="paymentFilterOptions" :plan-options="planFilterOptions" />

    <component :is="currentComponent" :pagination="pagination" :headers="headers" :list-data="listData"
      :user-roles-method="getUserRoles" :subscription-methods="subscriptionMethods" @load-items="loadItems"
      @update-pagination="updatePagination" />
  </div>
</template>

<script>
import { mapGetters } from 'vuex'
import helperMixin from '@/mixins/helper'
import userMixin from '@/mixins/user'
import ActiveUser from '@/components/user/ActiveUser.vue'
import ArchivedUser from '@/components/user/ArchivedUser.vue'
import UserFilterMenu from '@/components/user/UserFilterMenu.vue'
import UserFilterChips from '@/components/user/UserFilterChips.vue'
import { resourceTypes } from '@/shared/constants/generalList/list'
import roleTypes from '@/store/roles'
import PageBreadcrumbs from '@/components/common/PageBreadcrumbs.vue'

export default {
  components: {
    PageBreadcrumbs,
    ActiveUser,
    ArchivedUser,
    UserFilterMenu,
    UserFilterChips
  },
  mixins: [helperMixin, userMixin],
  data() {
    return {
      pagination: {
        search: '',
        role: [],
        status: [],
        payment: [],
        plan: [],
        currentPage: 1,
        itemsPerPage: 15,
        perPageOptions: [15, 25, 50, 100],
        totalItems: 0
      },
      listData: {
        users: [],
        loading: false,
        error: false,
        message: null
      }
    }
  },
  computed: {
    ...mapGetters(['isAdmin', 'isSuperCustomer']),

    isArchived() {
      return this.$route.name === 'users.archive.list'
    },

    currentComponent() {
      return this.isArchived ? 'ArchivedUser' : 'ActiveUser'
    },

    filters: {
      get() {
        return {
          role: this.pagination.role,
          status: this.pagination.status,
          payment: this.pagination.payment,
          plan: this.pagination.plan
        }
      },
      set(value) {
        this.pagination.role = value.role || []
        this.pagination.status = value.status || []
        this.pagination.payment = value.payment || []
        this.pagination.plan = value.plan || []
      }
    },

    // Only the Deletion Queue sits inside another page; the Users list opens from the sidebar.
    breadcrumbItems() {
      return [
        { title: 'Users', to: { name: 'users.index' } },
        { title: 'Deletion Queue' }
      ]
    },

    roleFilterOptions() {
      return [
        { title: 'Admin', value: roleTypes.ADMIN, icon: 'mdi-shield-account-outline' },
        { title: 'Customer', value: roleTypes.CUSTOMER, icon: 'mdi-account-outline' },
        { title: 'Super Customer', value: roleTypes.SUPER_CUSTOMER, icon: 'mdi-account-star-outline' }
      ]
    },

    statusFilterOptions() {
      return [
        { title: 'Active', value: 'active', icon: 'mdi-account-check-outline' },
        { title: 'Suspended', value: 'inactive', icon: 'mdi-account-off-outline' }
      ]
    },

    paymentFilterOptions() {
      return [
        { title: 'Paying', value: 'paying', icon: 'mdi-cash-check' },
        { title: 'Non-paying', value: 'non_paying', icon: 'mdi-cash-remove' }
      ]
    },

    planFilterOptions() {
      return [
        { title: 'Free Trial', value: 'free_trial', icon: 'mdi-timer-sand' },
        { title: 'Free', value: 'free', icon: 'mdi-gift-outline' },
        { title: 'Bronze', value: 'bronze', icon: 'mdi-medal-outline' },
        { title: 'Silver', value: 'silver', icon: 'mdi-medal-outline' },
        { title: 'Gold', value: 'gold', icon: 'mdi-medal-outline' },
        { title: 'Legacy', value: 'legacy', icon: 'mdi-archive-outline' }
      ]
    },

    headers() {
      if (this.isArchived) {
        return [
          { title: '#', key: 'serial_no', sortable: false, align: 'center' },
          { title: 'Name', key: 'name', sortable: true, width: '14%' },
          { title: 'Email', key: 'email', sortable: true, width: '22%' },
          { title: 'Plan', key: 'subscription', sortable: false },
          { title: 'Role', key: 'role', sortable: true },
          { title: 'Forms', key: 'forms_count', sortable: true, align: 'center' },
          { title: 'Total Leads', key: 'user_leads_count', sortable: true, align: 'center' },
          { title: 'Deletion Date', key: 'delete_at', sortable: true },
          { title: 'Reason', key: 'delete_source', sortable: true, align: 'center' }
        ]
      }

      return [
        { title: '#', key: 'serial_no', sortable: false, align: 'center' },
        { title: 'Name', key: 'name', sortable: true, width: '16%' },
        { title: 'Email', key: 'email', sortable: true, width: '22%' },
        { title: 'Role', key: 'role', sortable: true },
        { title: 'Plan', key: 'subscription', sortable: false },
        { title: 'Forms', key: 'forms_count', sortable: true, align: 'center' },
        { title: 'Total Leads', key: 'user_leads_count', sortable: true, align: 'center' },
        { title: 'Created At', key: 'created_at', sortable: true }
      ]
    },

    subscriptionMethods() {
      return {
        getUserSubscriptionTitle: this.getUserSubscriptionTitle,
        getUserSubscriptionStatus: this.getUserSubscriptionStatus
      }
    }
  },
  methods: {
    loadItems({ page, itemsPerPage, sortBy }) {
      this.setLoading(true)
      if (page) this.pagination.currentPage = page
      if (itemsPerPage) this.pagination.itemsPerPage = itemsPerPage

      const params = this.buildRequestParams({ page, sortBy })
      this.fetchUsers(params)
    },

    buildRequestParams({ page, sortBy }) {
      return {
        page: page || this.pagination.currentPage,
        per_page: this.pagination.itemsPerPage,
        search: {
          name: this.pagination.search || '',
          email: this.pagination.search || ''
        },
        filters: {
          role: this.pagination.role,
          status: this.pagination.status,
          is_paying: this.pagination.payment,
          plan: this.pagination.plan
        },
        sortField: sortBy?.length ? this.mapSortField(sortBy[0].key) : 'id',
        sortDirection: sortBy?.length ? sortBy[0].order : 'desc',
        archived: this.isArchived,
        resource: resourceTypes.USERS_LIST
      }
    },

    mapSortField(key) {
      const sortFieldMap = {
        'name': 'name',
        'email': 'email',
        'forms_count': 'forms_count',
        'user_leads_count': 'user_leads_count',
        'created_at': 'created_at'
      }
      return sortFieldMap[key] || key
    },

    async fetchUsers(params) {
      if (!this.isAdmin) {
        this.handleError({ message: 'Access denied. Admin privileges required.' })
        return
      }

      try {
        const response = await this.$store.dispatch('userlists/fetchUserLists', params)
        this.updateUsersData(response?.data)
        if (!this.isArchived) {
          await this.$store.dispatch('userlists/fetchUserListsSuccess', response?.data?.data || [])
        }
      } catch (err) {
        this.handleError(err)
        if (!this.isArchived) {
          await this.$store.dispatch('userlists/fetchUserListsSuccess', [])
        }
      } finally {
        this.setLoading(false)
      }
    },

    updateUsersData(data) {
      this.listData.users = data?.data || []
      this.pagination.totalItems = data?.pagination?.total || 0
      this.listData.error = false
      this.listData.message = null
    },

    setLoading(status) {
      this.listData.loading = status
    },

    handleError(err) {
      this.listData.error = true
      this.listData.message = err?.response?.data?.message || err?.message || 'An error occurred while loading users'
    },

    updatePagination(newPagination) {
      this.pagination = { ...this.pagination, ...newPagination }
    },

    getUserRoles(roles) {
      let rolesArray = []
      for (let role of roles) {
        rolesArray.push(role.title)
      }
      return rolesArray.join('<br>')
    },

    getUserSubscriptionStatus(user) {
      // TRIAL/TRIAL_EXPIRED are folded into the title itself (see
      // getUserSubscriptionTitle) - showing them again here would be redundant.
      const statusLabel = user.plan?.status_label
      if (statusLabel === 'TRIAL' || statusLabel === 'TRIAL_EXPIRED') {
        return ''
      }

      // Same status vocabulary the backend already uses for EmailOctopus/CRM Connect
      // (see User::resolveSubscriptionStatusLabel) - one vocabulary, everywhere,
      // so a cancelled/paused/expired subscription always shows correctly instead
      // of falling through to a blank status.
      return this.formatSubscriptionStatus(statusLabel)
    },

    getUserSubscriptionTitle(user) {
      const tierNames = { bronze: 'Bronze', silver: 'Silver', gold: 'Gold' }
      const statusLabel = user.plan?.status_label
      const tier = tierNames[user.plan?.plan_type]

      if (statusLabel === 'TRIAL') {
        return tier ? `Free Trial (${tier}/Trial)` : 'Free Trial'
      }
      if (statusLabel === 'TRIAL_EXPIRED') {
        return tier ? `Free Trial (${tier}/Expired)` : 'Free Trial'
      }

      return user.plan?.title || 'Free'
    },

    formatSubscriptionStatus(statusLabel) {
      const labels = {
        TRIAL: 'Trial',
        TRIAL_EXPIRED: 'Trial Expired',
        ACTIVE: 'Active',
        PAUSED: 'Paused',
        PAST_DUE: 'Past Due',
        CANCELED: 'Cancelled',
        PLAN_EXPIRED: 'Plan Expired',
        NO_PLAN: 'No Plan'
      }

      return labels[statusLabel] || ''
    }
  },
  watch: {
    'pagination.search': {
      handler() {
        this.pagination.currentPage = 1
        this.loadItems({})
      }
    },

    'pagination.role': {
      handler() {
        this.pagination.currentPage = 1
        this.loadItems({})
      }
    },

    'pagination.status': {
      handler() {
        this.pagination.currentPage = 1
        this.loadItems({})
      }
    },

    'pagination.payment': {
      handler() {
        this.pagination.currentPage = 1
        this.loadItems({})
      }
    },

    'pagination.plan': {
      handler() {
        this.pagination.currentPage = 1
        this.loadItems({})
      }
    },

    '$route'() {
      this.listData.users = []
      this.pagination = {
        ...this.pagination,
        search: '',
        role: [],
        status: [],
        payment: [],
        plan: [],
        currentPage: 1
      }
      this.loadItems({})
    }
  }
}
</script>
